import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import {
  fetchChatUsers,
  fetchChatMessages,
  sendChatMessage,
  markMessagesAsRead,
} from '../services/chatApi';
import './MessagesPage.css';

export default function MessagesPage() {
  const { user } = useContext(AuthContext);
  const { selectedRegion, allowedRegions } = useRegion();

  const isAdmin = user?.role === 'admin' || user?.email === 'willr@nezo-app.com' || user?.email?.includes('admin');
    
    console.log('[DEBUG] User role detection:', {
      user: user?.email,
      role: user?.role,
      isAdmin: isAdmin,
      allowedRegions: allowedRegions
    });
  const regionList = (allowedRegions?.length ? allowedRegions : [selectedRegion]).filter(Boolean);

  // UI state
  const [users, setUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [compose, setCompose] = useState('');
  const [sendRegion, setSendRegion] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadUpdatedAt, setThreadUpdatedAt] = useState(null);
  const [error, setError] = useState('');

  // Separate refs for different operations
  const pollRef = useRef(null);
  const usersInFlightRef = useRef(false);
  const threadInFlightRef = useRef(false);
  const debounceRef = useRef(null);

  // Scroll management refs
  const messagesContainerRef = useRef(null);
  const shouldScrollToBottom = useRef(true);
  const lastMessageCount = useRef(0);

  // Simple debounce function
  const debounce = useCallback((func, delay) => {
    return (...args) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => func(...args), delay);
    };
  }, []);

  /** ---- Helpers ---- **/

  // Deduplicate by id and sort ascending by created_at
  const normalizeMessages = useCallback((lists) => {
    const byId = new Map();
    lists.forEach(m => {
      if (!m?.id) return;
      byId.set(m.id, m);
    });
    const arr = Array.from(byId.values());
    arr.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
    return arr;
  }, []);

  // Smart scroll handler to preserve reading position
  const handleScrollUpdate = useCallback((newMessages) => {
    const container = messagesContainerRef.current;
    if (!container) {
      setMessages(newMessages);
      lastMessageCount.current = newMessages.length;
      return;
    }

    const wasAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
    const isNewMessage = newMessages.length > lastMessageCount.current;
    
    // Update messages
    setMessages(newMessages);
    lastMessageCount.current = newMessages.length;
    
    // Only auto-scroll if user was at bottom or if it's a new message and should scroll
    if (wasAtBottom || (isNewMessage && shouldScrollToBottom.current)) {
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 0);
    }
  }, []);

  // Track user scroll position
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
      shouldScrollToBottom.current = isAtBottom;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages.length]);

  // Load admin conversation list across regions
  const loadAdminUsers = useCallback(async () => {
    if (!isAdmin || usersInFlightRef.current) return;
    
    console.log('[MessagesPage] Loading admin users, current state:', {
      isAdmin,
      regionList,
      user: user?.email,
      userRole: user?.role
    });
    
    try {
      usersInFlightRef.current = true;
      setError('');
      const merged = new Map();

      for (let i = 0; i < regionList.length; i += 1) {
        const r = regionList[i];
        try {
          const list = await fetchChatUsers(r);
          console.log(`[MessagesPage] /chat/users region=${r} -> ${Array.isArray(list) ? list.length : 0}`);
          (Array.isArray(list) ? list : []).forEach(u => {
            const prev = merged.get(u.id) || {
              id: u.id, name: u.name, role: u.role, email: u.email,
              regions: {}, total_unread: 0, last_message_at: null,
            };
            prev.regions[r] = Number(u.unread_count || 0);
            prev.total_unread += Number(u.unread_count || 0);
            const curTime = u.last_message_at ? new Date(u.last_message_at).getTime() : 0;
            const prevTime = prev.last_message_at ? new Date(prev.last_message_at).getTime() : 0;
            if (curTime > prevTime) prev.last_message_at = u.last_message_at || prev.last_message_at;
            merged.set(u.id, prev);
          });
        } catch (e) {
          console.warn('[MessagesPage] fetchChatUsers failed for region', r, e);
        }
      }

      const finalList = Array.from(merged.values()).sort((a, b) => {
        if ((b.total_unread || 0) !== (a.total_unread || 0)) {
          return (b.total_unread || 0) - (a.total_unread || 0);
        }
        const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return tb - ta;
      });

      console.log('[MessagesPage] Final user list:', finalList);
      setUsers(finalList);
    } catch (e) {
      console.error('[MessagesPage] loadAdminUsers error:', e);
      setError('Failed to load conversations.');
      setUsers([]);
    } finally {
      usersInFlightRef.current = false;
    }
  }, [isAdmin, regionList, user?.email, user?.role]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Load a thread across *all* admin regions for a specific user
  const loadAdminThread = useCallback(async (uid) => {
    console.log(`[loadAdminThread] Called with uid: ${uid}, threadInFlightRef: ${threadInFlightRef.current}`);
    
    if (!uid || threadInFlightRef.current) {
      console.log(`[loadAdminThread] Early return - uid: ${uid}, threadInFlight: ${threadInFlightRef.current}`);
      return;
    }
    
    try {
      threadInFlightRef.current = true;
      setError('');
      setLoading(true);

      // Get regionList from the current value instead of closure
      const currentRegionList = (allowedRegions?.length ? allowedRegions : [selectedRegion]).filter(Boolean);
      console.log(`[MessagesPage] Loading thread for user ${uid} across regions:`, currentRegionList);

      const results = await Promise.allSettled(
        currentRegionList.map((r) => {
          console.log(`[loadAdminThread] Calling fetchChatMessages with userId: ${uid}, region: ${r}`);
          return fetchChatMessages(uid, r);
        })
      );

      let allMsgs = [];

      results.forEach((res, idx) => {
        const region = currentRegionList[idx];
        console.log(`[MessagesPage] Region ${region} result:`, {
          status: res.status,
          messageCount: res.status === 'fulfilled' ? (res.value?.messages?.length || 0) : 0,
          error: res.status === 'rejected' ? res.reason : null
        });
        
        if (res.status === 'fulfilled' && res.value) {
          const msgs = Array.isArray(res.value.messages) ? res.value.messages : [];
          console.log(`[MessagesPage] Region ${region}: ${msgs.length} messages`);
          
          const tagged = msgs.map((m) => ({ ...m, __region: region }));
          allMsgs.push(...tagged);
        } else {
          console.warn(`[MessagesPage] Failed to load messages for region ${region}:`, res.reason);
        }
      });

      allMsgs.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return da - db;
      });

      console.log(`[MessagesPage] Final result: ${allMsgs.length} total messages`);
      console.log(`[MessagesPage] Sample messages:`, allMsgs.slice(0, 2).map(m => ({
        id: m.id,
        sender: m.sender_name,
        recipient: m.recipient_id,
        message: m.message?.substring(0, 30),
        is_own: m.is_own_message,
        region: m.__region
      })));

      handleScrollUpdate(allMsgs);
      console.log(`[MessagesPage] Messages state updated, current length: ${allMsgs.length}`);
      setThreadUpdatedAt(new Date());

      const last = allMsgs[allMsgs.length - 1];
      setSendRegion(last?.__region || currentRegionList[0] || '');
      
    } catch (e) {
      console.error('[MessagesPage] loadAdminThread error:', e);
      setError('Failed to load messages.');
      setMessages([]);
    } finally {
      setLoading(false);
      threadInFlightRef.current = false;
    }
  }, [allowedRegions, selectedRegion, handleScrollUpdate]); // Include the dependencies it actually uses

  // Create debounced reload function  
  const debouncedReload = useCallback(() => {
    return debounce((uid) => {
      if (isAdmin && uid && !threadInFlightRef.current) {
        loadAdminThread(uid);
      }
    }, 1000);
  }, [debounce, isAdmin, loadAdminThread]);

  /** ---- Effects ---- **/

  // Initial load - FIXED: Remove function dependencies
  useEffect(() => {
    if (!user) return;
    
    console.log('[DEBUG] Initial load effect triggered');
    
    if (isAdmin) {
      loadAdminUsers();
    } else {
      // Simple direct call without function dependency issues
      const loadData = async () => {
        if (threadInFlightRef.current) return;
        
        try {
          threadInFlightRef.current = true;
          setError('');
          setLoading(true);
          
          const data = await fetchChatMessages(null, selectedRegion);
          const msgs = Array.isArray(data?.messages) ? data.messages : [];
          
          handleScrollUpdate(msgs);
          setThreadUpdatedAt(new Date());
          
          if (msgs.length > 0) {
            await markMessagesAsRead(null, selectedRegion);
          }
        } catch (e) {
          console.error('[DEBUG] Initial load error:', e);
          setError('Failed to load messages.');
          setMessages([]);
        } finally {
          setLoading(false);
          threadInFlightRef.current = false;
        }
      };
      
      loadData();
    }
  }, [user?.id, isAdmin, selectedRegion, handleScrollUpdate]); // FIXED: Only stable dependencies
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Admin user selection effect
  useEffect(() => {
    if (!isAdmin || !activeUser?.id || threadInFlightRef.current) return;
    
    console.log('[DEBUG] Admin user selection effect triggered');
    loadAdminThread(activeUser.id);
  }, [isAdmin, activeUser?.id, loadAdminThread]); // Removed loadAdminThread dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Polling effect - FIXED: Simplified to prevent loops
  useEffect(() => {
    if (!user) return;
    
    console.log('[DEBUG] Setting up polling');
    
    const interval = setInterval(() => {
      console.log('[DEBUG] Polling tick');
      
      if (isAdmin) {
        // Admin polling logic
        if (activeUser?.id && !threadInFlightRef.current) {
          console.log('[DEBUG] Polling: Loading admin thread');
          loadAdminThread(activeUser.id);
        }
        if (!usersInFlightRef.current) {
          loadAdminUsers();
        }
      } else {
        // User polling logic with scroll preservation
        if (!threadInFlightRef.current) {
          console.log('[DEBUG] Polling: Loading user thread');
          
          const pollUserMessages = async () => {
            try {
              threadInFlightRef.current = true;
              const data = await fetchChatMessages(null, selectedRegion);
              const msgs = Array.isArray(data?.messages) ? data.messages : [];
              handleScrollUpdate(msgs);
              setThreadUpdatedAt(new Date());
            } catch (e) {
              console.error('[DEBUG] Polling error:', e);
            } finally {
              threadInFlightRef.current = false;
            }
          };
          
          pollUserMessages();
        }
      }
    }, 15000); // Increased to 15 seconds to reduce load

    return () => {
      console.log('[DEBUG] Cleaning up polling');
      clearInterval(interval);
    };
  }, [user?.id, isAdmin, activeUser?.id, selectedRegion, handleScrollUpdate, loadAdminThread, loadAdminUsers]); // FIXED: Only stable dependencies
  // eslint-disable-next-line react-hooks/exhaustive-deps

  /** ---- Send ---- **/
  const handleSend = async (e) => {
    e.preventDefault();
    const text = compose.trim();
    if (!text) return;

    // Force scroll to bottom for new messages being sent
    shouldScrollToBottom.current = true;

    if (isAdmin) {
      if (!activeUser?.id) return;
      if (!sendRegion) { alert('Select a region to send into.'); return; }

      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const optimistic = {
        id: tempId,
        message: text,
        created_at: new Date().toISOString(),
        sender_name: user?.name || 'Me',
        sender_role: 'admin',
        is_own_message: true,
        __region: sendRegion,
      };
      
      handleScrollUpdate(normalizeMessages([...messages, optimistic]));
      setCompose('');

      try {
        const resp = await sendChatMessage({
          message: text,
          recipientId: activeUser.id,
          region: sendRegion,
        });
        
        console.log('[DEBUG] Send response:', resp);
        
        await loadAdminThread(activeUser.id);
        
      } catch (err) {
        console.error('[DEBUG] Send error:', err);
        handleScrollUpdate(messages.filter(m => m.id !== tempId));
        setError('Failed to send. Please try again.');
      }
      return;
    }

    // user/supplier send
    const tempId = `t-${Date.now()}`;
    const optimistic = {
      id: tempId,
      message: text,
      created_at: new Date().toISOString(),
      sender_name: user?.name || 'Me',
      sender_role: user?.role || 'user',
      is_own_message: true,
    };
    
    handleScrollUpdate([...messages, optimistic]);
    setCompose('');

    try {
      await sendChatMessage({ message: text, recipientId: null, region: selectedRegion });
      // Reload to get the actual message from server
      const data = await fetchChatMessages(null, selectedRegion);
      const msgs = Array.isArray(data?.messages) ? data.messages : [];
      handleScrollUpdate(msgs);
    } catch (err) {
      handleScrollUpdate(messages.filter(m => m.id !== tempId));
      setError('Failed to send. Please try again.');
    }
  };

  /** ---- Render ---- **/
  const RegionChip = ({ r }) => (
    <span className="region-chip" title={`Region: ${r}`}>{r}</span>
  );

  // Add this right before the return statement to debug the render state
  console.log('[DEBUG] Render state:', {
    isAdmin,
    loading,
    messagesLength: messages.length,
    error,
    activeUser: activeUser?.id,
    user: user?.email
  });

  return (
    <div className={`messages-page ${!isAdmin ? 'user-view' : ''}`}>
      {isAdmin && (
        <aside className="mp-left">
          <div className="mp-left-title">Conversations</div>

          {users.length === 0 ? (
            <div className="mp-empty">No users with messages found across your regions.</div>
          ) : (
            users.map(u => (
              <div
                key={u.id}
                className={`mp-user ${activeUser?.id === u.id ? 'active' : ''}`}
                onClick={() => {
                  console.log('[MessagesPage] User clicked:', {
                    id: u.id,
                    name: u.name,
                    role: u.role
                  });
                  setActiveUser(u);
                }}
              >
                <div className="mp-user-name">
                  {u.name} <span className="mp-user-role">({u.role})</span>
                </div>
                <div className="mp-user-right">
                  <div className={`mp-user-unread ${u.total_unread ? 'has' : ''}`}>
                    {u.total_unread || 0}
                  </div>
                  <div className="mp-user-regions">
                    {Object.entries(u.regions || {}).map(([r, unread]) => (
                      <span key={r} className="mp-user-region-chip" title={`${r} • ${unread} unread`}>
                        {r}{unread ? ` (${unread})` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}

          <button
            className="mp-refresh"
            type="button"
            onClick={() => {
              if (isAdmin) loadAdminUsers();
            }}
          >
            Refresh
          </button>
        </aside>
      )}

      <main className="mp-right">
        <header className="mp-header">
          {isAdmin ? (
            activeUser ? (
              <>
                <div className="mp-title">
                  {activeUser.name} <span className="mp-user-role">({activeUser.role})</span>
                </div>
                <div className="mp-actions">
                  <select
                    className="mp-region-select"
                    value={sendRegion}
                    onChange={e => setSendRegion(e.target.value)}
                    title="Send to region"
                  >
                    <option value="" disabled>Send to region…</option>
                    {regionList.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button
                    className="mp-refresh"
                    type="button"
                    onClick={() => activeUser?.id && loadAdminThread(activeUser.id)}
                  >
                    Refresh
                  </button>
                </div>
              </>
            ) : (
              <div className="mp-title">Select a conversation</div>
            )
          ) : (
            <div className="mp-title">Support Chat</div>
          )}

          <div className="mp-updated">
            {threadUpdatedAt ? `Thread updated ${threadUpdatedAt.toLocaleTimeString()}` : ''}
          </div>
        </header>

        <section className="mp-thread" ref={messagesContainerRef}>
          {error && <div className="mp-error">{error}</div>}
          {loading ? (
            <div className="mp-empty">Loading messages…</div>
          ) : messages.length === 0 ? (
            <div className="mp-empty">
              {isAdmin && !activeUser ? 'Select a conversation' : 'No messages yet. Start the conversation!'}
            </div>
          ) : (
            messages.map(m => (
              <div key={m.id} className={`mp-msg ${m.is_own_message ? 'own' : 'other'}`}>
                <div className="mp-bubble">
                  <div className="mp-text">{m.message}</div>
                  <div className="mp-meta">
                    <span>{new Date(m.created_at).toLocaleString()}</span>
                    {m.__region && <RegionChip r={m.__region} />}
                  </div>
                </div>
              </div>
            ))
          )}
        </section>

        {/* Composer */}
        {(!isAdmin || activeUser) && (
          <form className="mp-composer" onSubmit={handleSend}>
            {!isAdmin && (
              <div className="mp-region-pill" title="Current region">{selectedRegion}</div>
            )}
            <textarea
              rows={2}
              className="mp-textarea"
              placeholder="Type your message…"
              value={compose}
              onChange={e => setCompose(e.target.value)}
              onInput={e => {
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
              }}
            />
            <button
              className="mp-send"
              disabled={!compose.trim() || (isAdmin && activeUser && !sendRegion)}
            >
              Send
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
